import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, UploadSimple, FilePdf, CheckCircle, ArrowCounterClockwise,
  Copy, Trash, MapPin, DownloadSimple, ChatText, SealCheck, Sparkle,
  LinkSimple, ArrowsClockwise, Receipt, EnvelopeSimple, ClockCounterClockwise, Eye,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Sidebar } from "@/components/Sidebar";
import { PaymentBadge, ReportBadge, PaymentMomentBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function DossierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const provRef = useRef();
  const defRef = useRef();
  const docRef = useRef();
  const [billitOpen, setBillitOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [invSearch, setInvSearch] = useState("");
  const [comment, setComment] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/dossiers/${id}`);
      setD(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      navigate("/admin");
    }
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => { setComment(d?.comment || ""); }, [d?.id]);

  const saveComment = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/dossiers/${id}/comment`, { comment });
      setD(data);
      toast.success("Commentaire enregistré");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copié`); };

  const uploadVersion = async (file, kind) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Seuls les PDF sont acceptés"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    const endpoint = kind === "definitive" ? "upload-definitive" : "upload-provisional";
    try {
      const { data } = await api.post(`/dossiers/${id}/${endpoint}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setD(data);
      toast.success(kind === "definitive" ? "Rapport définitif posté — email envoyé" : "Rapport provisoire envoyé au client");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const togglePaid = async () => {
    setBusy(true);
    try {
      const endpoint = d.payment_status === "paid" ? "mark-unpaid" : "mark-paid";
      const { data } = await api.post(`/dossiers/${id}/${endpoint}`);
      setD(data);
      toast.success(data.payment_status === "paid" ? "Marqué comme payé" : "Paiement annulé");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/dossiers/${id}`);
      toast.success("Dossier supprimé");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openBillit = async () => {
    setBillitOpen(true);
    setInvLoading(true);
    try {
      const { data } = await api.get("/billit/invoices");
      setInvoices(data);
    } catch (err) {
      toast.error("Impossible de charger les factures Billit");
    } finally {
      setInvLoading(false);
    }
  };

  const linkInvoice = async (inv) => {
    try {
      const { data } = await api.post(`/dossiers/${id}/link-billit`, { order_id: inv.order_id, order_number: inv.order_number });
      setD(data);
      setBillitOpen(false);
      toast.success(data.payment_status === "paid" ? "Facture liée — paiement détecté ✅" : "Facture Billit liée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const unlinkBillit = async () => {
    try {
      const { data } = await api.post(`/dossiers/${id}/unlink-billit`);
      setD(data);
      toast.success("Facture déliée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const checkBillit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dossiers/${id}/check-billit`);
      setD(data);
      toast.success(data.payment_status === "paid" ? "Paiement confirmé via Billit ✅" : "Pas encore payé selon Billit");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const sendCodeEmail = async () => {
    setBusy(true);
    try {
      await api.post(`/dossiers/${id}/send-code-email`);
      toast.success("Email envoyé au client");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const downloadVersion = async (v) => {
    try {
      const res = await api.get(`/dossiers/${id}/download/${v.version}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (err) {
      toast.error("Impossible d'ouvrir le rapport");
    }
  };

  const uploadDocument = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Seuls les PDF sont acceptés"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/dossiers/${id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setD(data);
      toast.success("Fichier ajouté");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deleteDocument = async (docId) => {
    try {
      const { data } = await api.delete(`/dossiers/${id}/documents/${docId}`);
      setD(data);
      toast.success("Fichier supprimé");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const res = await api.get(`/dossiers/${id}/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (err) {
      toast.error("Impossible d'ouvrir le fichier");
    }
  };

  const notifyDocuments = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dossiers/${id}/notify-documents`);
      setD(data);
      toast.success("Email envoyé au client");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const viewEmailContent = async (index) => {
    try {
      const res = await api.get(`/dossiers/${id}/email-log/${index}/content`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (err) {
      toast.error("Impossible d'ouvrir l'email");
    }
  };

  const downloadEmailPdf = async (index) => {
    try {
      const res = await api.get(`/dossiers/${id}/email-log/${index}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `preuve-email-${index}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Impossible de générer le PDF");
    }
  };

  if (!d) return <div className="ml-64 p-8 text-slate-400">Chargement…</div>;

  const paid = d.payment_status === "paid";
  const rs = d.report_status;
  const hasVersions = d.versions.length > 0;

  const InfoRow = ({ label, value, mono, action }) => (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <span className="label-eyebrow">{label}</span>
      <div className="flex items-center gap-2">
        <span className={`text-slate-900 ${mono ? "font-mono text-sm" : "font-medium"}`}>{value}</span>
        {action}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="ml-64 p-8 max-w-4xl" data-testid="dossier-detail-page">
        <button onClick={() => navigate("/admin")} data-testid="back-button"
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6 transition-colors">
          <ArrowLeft size={16} /> Retour aux dossiers
        </button>

        <div className="flex items-start justify-between mb-8">
          <div>
            <p className="label-eyebrow mb-1">Dossier</p>
            <h1 className="font-heading text-3xl font-medium tracking-tight text-slate-950">{d.client_name}</h1>
            <div className="flex items-center gap-1.5 text-slate-500 mt-1.5">
              <MapPin size={15} /> {d.property_address}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <PaymentBadge status={d.payment_status} />
            <ReportBadge status={rs} paid={d.payment_status === "paid"} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 items-start">
          {/* Left */}
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <h3 className="font-heading text-lg font-medium mb-3 text-slate-900">Détails</h3>
              <InfoRow label="Email" value={d.client_email} />
              <InfoRow label="Montant" value={`${d.price.toFixed(2)} €`} />
              <InfoRow label="Paiement" value={<PaymentMomentBadge label={d.payment_moment_label} />} />
              <InfoRow label="Réf. facture Billit" value={d.payment_ref} mono
                action={<button data-testid="copy-payment-ref" onClick={() => copy(d.payment_ref, "Référence")}
                  className="text-slate-400 hover:text-blue-600"><Copy size={16} /></button>} />
              <InfoRow label="Code d'accès actuel" value={d.access_code} mono
                action={<button data-testid="copy-access-code" onClick={() => copy(d.access_code, "Code")}
                  className="text-slate-400 hover:text-blue-600"><Copy size={16} /></button>} />
              <Button variant="outline" data-testid="send-code-email-button" onClick={sendCodeEmail} disabled={busy}
                className="w-full h-10 mt-4">
                <EnvelopeSimple size={16} className="mr-1.5" />
                Envoyer l'email au client {d.billit_order_id ? "(code + facture)" : "(code)"}
              </Button>
              {d.last_email && (
                <p className="text-xs text-slate-400 mt-2 text-center" data-testid="last-email-info">
                  Dernier email : <span className="text-slate-600 font-medium">{d.last_email.label}</span>
                  {" · "}
                  {new Date(d.last_email.at).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short" })}
                </p>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-6">
              <h3 className="font-heading text-lg font-medium mb-4 text-slate-900">Paiement</h3>
              <Button data-testid="toggle-paid-button" onClick={togglePaid} disabled={busy}
                className={`w-full h-11 ${paid
                  ? "bg-white text-slate-900 border border-slate-200 hover:bg-slate-50"
                  : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}>
                {paid ? (<><ArrowCounterClockwise size={18} className="mr-1.5" /> Annuler le paiement</>)
                     : (<><CheckCircle size={18} weight="bold" className="mr-1.5" /> Marquer comme payé</>)}
              </Button>
              <p className="text-xs text-slate-400 mt-3">
                Ou reliez la facture Billit pour une détection <strong>automatique</strong> du paiement :
              </p>
              {d.billit_order_id ? (
                <div className="mt-3 rounded-md border border-slate-200 p-3" data-testid="billit-linked">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-slate-800">
                      <Receipt size={16} className="text-blue-600" />
                      Facture Billit <strong>{d.billit_order_number}</strong>
                    </div>
                    <button onClick={unlinkBillit} data-testid="unlink-billit"
                      className="text-xs text-slate-400 hover:text-red-600">Délier</button>
                  </div>
                  <Button variant="outline" data-testid="check-billit-button" onClick={checkBillit} disabled={busy}
                    className="w-full h-9 mt-3 text-sm">
                    <ArrowsClockwise size={15} className="mr-1.5" /> Vérifier le paiement maintenant
                  </Button>
                  <p className="text-xs text-slate-400 mt-2">Vérification automatique toutes les 15 min.</p>
                </div>
              ) : (
                <Button variant="outline" data-testid="link-billit-button" onClick={openBillit}
                  className="w-full h-10 mt-3">
                  <LinkSimple size={16} className="mr-1.5" /> Lier une facture Billit
                </Button>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="email-history-card">
              <h3 className="font-heading text-lg font-medium mb-1 text-slate-900 flex items-center gap-2">
                <ClockCounterClockwise size={18} className="text-blue-600" /> Historique des emails
              </h3>
              <p className="text-xs text-slate-400 mb-4">
                Preuve d'envoi — une copie de chaque email est aussi envoyée en copie cachée à votre Gmail.
              </p>
              {(!d.email_log || d.email_log.length === 0) ? (
                <p className="text-sm text-slate-400">Aucun email envoyé pour ce dossier.</p>
              ) : (
                <ul className="space-y-2" data-testid="email-log-list">
                  {[...d.email_log].reverse().map((e) => (
                    <li key={e.index}
                      className="flex items-center justify-between border border-slate-100 rounded-md px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-sm text-slate-800 font-medium truncate">{e.label}</p>
                        <p className="text-xs text-slate-400 truncate">
                          {new Date(e.at).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short" })}
                          {" · "}{e.to}
                        </p>
                      </div>
                      {e.has_content && (
                        <div className="shrink-0 ml-3 flex items-center gap-3">
                          <button data-testid={`view-email-${e.index}`} onClick={() => viewEmailContent(e.index)}
                            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                            <Eye size={14} /> Voir
                          </button>
                          <button data-testid={`pdf-email-${e.index}`} onClick={() => downloadEmailPdf(e.index)}
                            className="text-xs text-slate-500 hover:text-red-600 flex items-center gap-1">
                            <FilePdf size={14} /> PDF
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Right: workflow */}
          <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="report-workflow">
            <h3 className="font-heading text-lg font-medium mb-1 text-slate-900">Rapport</h3>
            <p className="text-sm text-slate-500 mb-1">{d.report_status_label}</p>
            <p className="text-xs mb-5 text-slate-400">
              {d.requires_validation ? "Circuit avec acceptation du client" : "Rapport direct (sans acceptation)"}
            </p>

            <div className="mb-5">
              <p className="label-eyebrow mb-1.5">Commentaire au client (email du rapport)</p>
              <Textarea data-testid="comment-textarea" value={comment} onChange={(e) => setComment(e.target.value)}
                rows={3} className="resize-none"
                placeholder="Ex. Résultat médiocre ; fournissez l'isolation de la toiture pour l'améliorer." />
              <Button variant="outline" size="sm" data-testid="save-comment-button" onClick={saveComment}
                disabled={busy || comment === (d.comment || "")} className="mt-2">
                Enregistrer le commentaire
              </Button>
            </div>

            {!paid && (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800 mb-5">
                En attente du paiement. Le client ne pourra télécharger le rapport qu'une fois le paiement confirmé.
              </div>
            )}

            {rs === "validated" && (
              <div className="rounded-md bg-violet-50 border border-violet-200 p-4 text-sm text-violet-800 mb-5 flex items-start gap-2" data-testid="validated-notice">
                <SealCheck size={20} weight="fill" className="text-violet-600 shrink-0" />
                <span>{d.tacit_accepted
                  ? <>Rapport <strong>accepté automatiquement</strong> — le client n'a pas réagi dans le délai. Postez maintenant le rapport définitif.</>
                  : <>Le client a <strong>validé</strong> le rapport provisoire. Postez maintenant le rapport définitif.</>}</span>
              </div>
            )}

            {rs === "provisional" && d.requires_validation && (
              <div className="rounded-md bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800 mb-5" data-testid="tacit-pending-notice">
                En attente de la réaction du client. Sans réponse, le rapport sera automatiquement considéré comme accepté.
                {d.tacit_reminder_at && <span className="block mt-1 text-blue-600">Rappel d'acceptation tacite déjà envoyé au client.</span>}
              </div>
            )}

            {rs === "revision_requested" && (
              <div className="rounded-md bg-orange-50 border border-orange-200 p-4 text-sm text-orange-800 mb-5">
                Le client a demandé des corrections (voir remarques ci-dessous). Postez une nouvelle version provisoire.
              </div>
            )}

            {/* Upload buttons */}
            {!d.requires_validation ? (
              rs !== "definitive" && (
                <button data-testid="upload-definitive-button" onClick={() => defRef.current?.click()} disabled={busy}
                  className="w-full border-2 border-dashed border-emerald-300 rounded-md p-6 text-center hover:border-emerald-500 hover:bg-emerald-50/50 transition-colors mb-5">
                  <Sparkle size={26} className="mx-auto text-emerald-500 mb-2" weight="fill" />
                  <p className="text-sm text-slate-700 font-medium">{busy ? "Envoi…" : "Poster le rapport définitif (PDF)"}</p>
                  <p className="text-xs text-slate-400 mt-1">Rapport direct : téléchargeable par le client dès le paiement.</p>
                </button>
              )
            ) : rs === "validated" ? (
              <button data-testid="upload-definitive-button" onClick={() => defRef.current?.click()} disabled={busy}
                className="w-full border-2 border-dashed border-violet-300 rounded-md p-6 text-center hover:border-violet-500 hover:bg-violet-50/50 transition-colors mb-5">
                <Sparkle size={26} className="mx-auto text-violet-500 mb-2" weight="fill" />
                <p className="text-sm text-slate-700 font-medium">{busy ? "Envoi…" : "Poster le rapport DÉFINITIF (PDF)"}</p>
              </button>
            ) : (
              <button data-testid="upload-provisional-button" onClick={() => provRef.current?.click()} disabled={busy}
                className="w-full border-2 border-dashed border-slate-300 rounded-md p-6 text-center hover:border-blue-500 hover:bg-blue-50/50 transition-colors mb-5">
                <UploadSimple size={26} className="mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-700 font-medium">
                  {busy ? "Envoi…" : hasVersions ? "Poster une nouvelle version provisoire" : "Poster le rapport provisoire (PDF)"}
                </p>
                <p className="text-xs text-slate-400 mt-1">Un nouveau code sera envoyé au client à chaque nouvelle version.</p>
              </button>
            )}
            <input ref={provRef} type="file" accept="application/pdf" className="hidden"
              data-testid="prov-file-input" onChange={(e) => uploadVersion(e.target.files[0], "provisional")} />
            <input ref={defRef} type="file" accept="application/pdf" className="hidden"
              data-testid="def-file-input" onChange={(e) => uploadVersion(e.target.files[0], "definitive")} />

            {/* Versions */}
            {hasVersions && (
              <div className="mb-5">
                <p className="label-eyebrow mb-2">Versions</p>
                <div className="space-y-2">
                  {d.versions.map((v) => (
                    <div key={v.version} data-testid={`version-${v.version}`}
                      className="flex items-center gap-2.5 p-2.5 rounded-md bg-slate-50 border border-slate-200">
                      <FilePdf size={20} weight="fill" className={v.kind === "definitive" ? "text-emerald-500" : "text-red-500"} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{v.filename}</p>
                        <p className="text-xs text-slate-500">
                          v{v.version} · {v.kind === "definitive" ? "Définitif" : "Provisoire"} · code {v.access_code}
                        </p>
                      </div>
                      <button data-testid={`download-version-${v.version}`} onClick={() => downloadVersion(v)}
                        className="text-slate-400 hover:text-blue-600"><DownloadSimple size={18} /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Remarks */}
            {d.remarks.length > 0 && (
              <div>
                <p className="label-eyebrow mb-2 flex items-center gap-1.5"><ChatText size={14} /> Messages / remarques du client</p>
                <div className="space-y-2">
                  {d.remarks.map((r, i) => (
                    <div key={i} data-testid={`remark-${i}`}
                      className="text-sm text-slate-700 bg-orange-50 border border-orange-100 rounded-md p-3">
                      {r.text}
                      <span className="block text-xs text-slate-400 mt-1">sur la version {r.version}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6 mt-6" data-testid="documents-section">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-heading text-lg font-medium text-slate-900">Fichiers supplémentaires</h3>
            {d.documents?.length > 0 && (
              <Button variant="outline" size="sm" data-testid="notify-documents-button" onClick={notifyDocuments} disabled={busy}>
                <ChatText size={15} className="mr-1.5" /> Prévenir le client
              </Button>
            )}
          </div>
          <p className="text-xs text-slate-400 mb-4">
            Ajoutez un ou plusieurs rapports définitifs (ex. un par appartement ou par chaudière).
            Le client les télécharge avec le même code, une fois le paiement confirmé.
          </p>
          <button data-testid="upload-document-button" onClick={() => docRef.current?.click()} disabled={busy}
            className="w-full border-2 border-dashed border-slate-300 rounded-md p-5 text-center hover:border-blue-500 hover:bg-blue-50/50 transition-colors">
            <UploadSimple size={24} className="mx-auto text-slate-400 mb-2" />
            <p className="text-sm text-slate-700 font-medium">{busy ? "Envoi…" : "Ajouter un fichier PDF"}</p>
          </button>
          <input ref={docRef} type="file" accept="application/pdf" className="hidden"
            data-testid="document-file-input"
            onChange={(e) => { uploadDocument(e.target.files[0]); e.target.value = ""; }} />
          {d.documents?.length > 0 && (
            <div className="space-y-2 mt-4">
              {d.documents.map((doc) => (
                <div key={doc.id} data-testid={`document-${doc.id}`}
                  className="flex items-center gap-2.5 p-2.5 rounded-md bg-slate-50 border border-slate-200">
                  <FilePdf size={20} weight="fill" className="text-emerald-500" />
                  <p className="text-sm truncate flex-1 min-w-0">{doc.filename}</p>
                  <button data-testid={`download-document-${doc.id}`} onClick={() => downloadDocument(doc)}
                    className="text-slate-400 hover:text-blue-600"><DownloadSimple size={18} /></button>
                  <button data-testid={`delete-document-${doc.id}`} onClick={() => deleteDocument(doc.id)}
                    className="text-slate-400 hover:text-red-600"><Trash size={17} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="delete-dossier-button"
                className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 transition-colors">
                <Trash size={16} /> Supprimer ce dossier
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Supprimer le dossier ?</AlertDialogTitle>
                <AlertDialogDescription>
                  Cette action est irréversible. Le dossier de {d.client_name} sera définitivement supprimé.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction data-testid="confirm-delete" onClick={remove}
                  className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
        <Dialog open={billitOpen} onOpenChange={setBillitOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Lier une facture Billit</DialogTitle></DialogHeader>
            <Input placeholder="Rechercher (n° ou client)…" value={invSearch}
              onChange={(e) => setInvSearch(e.target.value)} data-testid="billit-search" className="mb-1" />
            <div className="max-h-96 overflow-y-auto space-y-2">
              {invLoading ? (
                <p className="text-sm text-slate-400 p-4 text-center">Chargement des factures…</p>
              ) : (() => {
                const list = invoices.filter((inv) =>
                  `${inv.order_number} ${inv.customer || ""}`.toLowerCase().includes(invSearch.toLowerCase()));
                if (list.length === 0)
                  return <p className="text-sm text-slate-400 p-4 text-center">Aucune facture trouvée.</p>;
                return list.map((inv) => (
                  <button key={inv.order_id} data-testid={`billit-invoice-${inv.order_id}`}
                    onClick={() => linkInvoice(inv)}
                    className="w-full text-left border border-slate-200 rounded-md p-3 hover:border-blue-400 hover:bg-blue-50/40 transition-colors flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-slate-900 truncate">{inv.order_number} — {inv.customer || "—"}</p>
                      <p className="text-xs text-slate-500">
                        {typeof inv.total === "number" ? inv.total.toFixed(2) : inv.total} € · {(inv.order_date || "").slice(0, 10)}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded shrink-0 ${inv.paid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {inv.paid ? "Payé" : "À payer"}
                    </span>
                  </button>
                ));
              })()}
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
